/**
 * Schedule Editor Store
 *
 * Zustand store for managing schedule editor state with undo/redo support.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  ParsedSchedule,
  SchedulePeriod,
  DaySchedule,
  TimeValue,
  DayType,
  ScheduleView,
  ScheduleEditMode,
  ScheduleAction,
  ScheduleClipboard,
  ScheduleCategory,
  ScheduleInfo,
  ScheduleReference,
  ScheduleTypeLimits,
  ScheduleTemplate,
} from './types';
import { parseSchedule, parseScheduleTypeLimits, getScheduleObjects, getScheduleTypeLimitsObjects } from './parser';
import { createDefaultScheduleCompact, createDefaultScheduleConstant, scheduleToIDFObject } from './serializer';
import type { IDFDocument, IDFObject } from '../parser';

/**
 * Maximum history size for undo/redo
 */
const MAX_HISTORY_SIZE = 50;

/**
 * Generate a unique ID
 */
function generateId(): string {
  return `sch_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Deep clone a schedule for undo/redo
 */
function cloneSchedule(schedule: ParsedSchedule): ParsedSchedule {
  return JSON.parse(JSON.stringify(schedule)) as ParsedSchedule;
}

/**
 * Categorize a schedule based on name patterns and usage
 */
function categorizeSchedule(schedule: ParsedSchedule, references: ScheduleReference[]): ScheduleCategory {
  const name = schedule.name.toLowerCase();

  // Pattern matching on name
  if (/occup|people|person/.test(name)) return 'occupancy';
  if (/light|illum/.test(name)) return 'lighting';
  if (/equip|plug|process/.test(name)) return 'equipment';
  if (/heat|htg/.test(name) && /cool|clg/.test(name)) return 'thermostat';
  if (/heat|htg|setpoint/.test(name) && !/cool|clg/.test(name)) return 'heating';
  if (/cool|clg|setpoint/.test(name) && !/heat|htg/.test(name)) return 'cooling';
  if (/avail|operation|fan|pump/.test(name)) return 'hvac';
  if (/infil|ventil|oa|outdoor/.test(name)) return 'ventilation';
  if (/activity/.test(name)) return 'activity';
  if (/on|off|binary|fraction/.test(name)) return 'availability';

  // Fall back to usage-based categorization
  if (references.some((r) => r.objectClass.includes('People'))) return 'occupancy';
  if (references.some((r) => r.objectClass.includes('Lights'))) return 'lighting';
  if (references.some((r) => r.objectClass.includes('Equipment'))) return 'equipment';
  if (references.some((r) => r.objectClass.includes('Thermostat'))) return 'thermostat';
  if (references.some((r) => r.fieldName.toLowerCase().includes('heating'))) return 'heating';
  if (references.some((r) => r.fieldName.toLowerCase().includes('cooling'))) return 'cooling';

  return 'other';
}

/**
 * Find all references to a schedule in the IDF document
 */
function findScheduleReferences(scheduleName: string, document: IDFDocument | null): ScheduleReference[] {
  if (!document) return [];

  const references: ScheduleReference[] = [];
  const lowerName = scheduleName.toLowerCase();

  for (const obj of document.objects) {
    // Skip schedule objects themselves
    if (obj.className.startsWith('Schedule:')) continue;

    for (let i = 0; i < obj.fields.length; i++) {
      const field = obj.fields[i];
      if (field && field.value.toString().toLowerCase() === lowerName) {
        // Check if the field comment suggests it's a schedule reference
        const comment = field.inlineComment?.toLowerCase() || '';
        if (comment.includes('schedule') || comment.includes('availability')) {
          references.push({
            objectClass: obj.className,
            objectName: obj.fields[0]?.value?.toString() || '',
            fieldName: field.inlineComment || `Field ${String(i + 1)}`,
          });
        }
      }
    }
  }

  return references;
}

interface ScheduleEditorState {
  // Current state
  schedules: ParsedSchedule[];
  typeLimits: ScheduleTypeLimits[];
  selectedScheduleId: string | null;
  selectedPeriodId: string | null;
  selectedDayType: DayType | null;
  view: ScheduleView;
  editMode: ScheduleEditMode;

  // Template state
  templateFilter: {
    buildingType: string | null;
    category: ScheduleCategory | null;
    searchQuery: string;
  };

  // Undo/redo
  undoStack: ScheduleAction[];
  redoStack: ScheduleAction[];

  // Clipboard
  clipboard: ScheduleClipboard | null;

  // Dirty flag
  isDirty: boolean;

  // Track original IDF object IDs to detect deletions
  originalIdfObjectIds: Set<string>;

  // Actions
  loadFromDocument: (document: IDFDocument) => void;
  selectSchedule: (id: string | null) => void;
  selectPeriod: (id: string | null) => void;
  selectDayType: (dayType: DayType | null) => void;
  setView: (view: ScheduleView) => void;
  setEditMode: (mode: ScheduleEditMode) => void;

  // Schedule CRUD
  createSchedule: (name: string, typeLimits: string | null, type?: 'compact' | 'constant') => string;
  deleteSchedule: (id: string) => void;
  duplicateSchedule: (id: string, newName: string) => string | null;
  updateSchedule: (id: string, updates: Partial<ParsedSchedule>) => void;

  // Period operations
  addPeriod: (scheduleId: string, period: Omit<SchedulePeriod, 'id'>) => string | null;
  deletePeriod: (scheduleId: string, periodId: string) => void;
  updatePeriod: (scheduleId: string, periodId: string, updates: Partial<SchedulePeriod>) => void;

  // Day schedule operations
  updateDayValues: (
    scheduleId: string,
    periodId: string,
    dayTypes: DayType[],
    values: TimeValue[]
  ) => void;
  copyDayToOthers: (
    scheduleId: string,
    periodId: string,
    sourceDayTypes: DayType[],
    targetDayTypes: DayType[][]
  ) => void;

  // Template operations
  setTemplateFilter: (filter: Partial<ScheduleEditorState['templateFilter']>) => void;
  applyTemplate: (template: ScheduleTemplate, scheduleName: string) => string;

  // Clipboard operations
  copyToClipboard: (type: ScheduleClipboard['type'], data: ScheduleClipboard['data']) => void;
  pasteFromClipboard: (scheduleId: string, periodId?: string) => void;

  // Undo/redo
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // Sync to IDF document
  syncToDocument: (
    updateObject: (objectId: string, updates: Partial<IDFObject>) => void,
    addObject: (obj: Omit<IDFObject, 'id'>) => string,
    removeObject: (objectId: string) => void
  ) => void;

  // Utility
  getScheduleInfo: (schedule: ParsedSchedule, document: IDFDocument | null) => ScheduleInfo;
  getSchedule: (id: string) => ParsedSchedule | undefined;
  getTypeLimits: (name: string) => ScheduleTypeLimits | undefined;
  clear: () => void;
  markClean: () => void;
}

/**
 * Push an action to the undo stack
 */
function pushUndo(state: ScheduleEditorState, action: ScheduleAction): Partial<ScheduleEditorState> {
  const undoStack = [...state.undoStack, action];
  if (undoStack.length > MAX_HISTORY_SIZE) {
    undoStack.shift();
  }
  return {
    undoStack,
    redoStack: [], // Clear redo stack on new action
    isDirty: true,
  };
}

export const useScheduleStore = create<ScheduleEditorState>()(
  devtools(
    (set, get) => ({
      schedules: [],
      typeLimits: [],
      selectedScheduleId: null,
      selectedPeriodId: null,
      selectedDayType: null,
      view: 'week',
      editMode: 'select',
      templateFilter: {
        buildingType: null,
        category: null,
        searchQuery: '',
      },
      undoStack: [],
      redoStack: [],
      clipboard: null,
      isDirty: false,
      originalIdfObjectIds: new Set<string>(),

      loadFromDocument: (document: IDFDocument) => {
        // Parse schedule type limits
        const typeLimitsObjects = getScheduleTypeLimitsObjects(document.objects);
        const typeLimits: ScheduleTypeLimits[] = [];
        for (const obj of typeLimitsObjects) {
          const parsed = parseScheduleTypeLimits(obj);
          if (parsed) {
            typeLimits.push(parsed);
          }
        }

        // Parse schedules and track their IDF object IDs
        const scheduleObjects = getScheduleObjects(document.objects);
        const schedules: ParsedSchedule[] = [];
        const originalIdfObjectIds = new Set<string>();
        for (const obj of scheduleObjects) {
          const parsed = parseSchedule(obj);
          if (parsed) {
            schedules.push(parsed);
            if (parsed.idfObjectId) {
              originalIdfObjectIds.add(parsed.idfObjectId);
            }
          }
        }

        set({
          schedules,
          typeLimits,
          selectedScheduleId: null,
          selectedPeriodId: null,
          undoStack: [],
          redoStack: [],
          isDirty: false,
          originalIdfObjectIds,
        });
      },

      selectSchedule: (id: string | null) => {
        const state = get();
        set({
          selectedScheduleId: id,
          selectedPeriodId: id
            ? state.schedules.find((s) => s.id === id)?.periods[0]?.id || null
            : null,
          selectedDayType: null,
        });
      },

      selectPeriod: (id: string | null) => {
        set({ selectedPeriodId: id, selectedDayType: null });
      },

      selectDayType: (dayType: DayType | null) => {
        set({ selectedDayType: dayType });
      },

      setView: (view: ScheduleView) => {
        set({ view });
      },

      setEditMode: (mode: ScheduleEditMode) => {
        set({ editMode: mode });
      },

      createSchedule: (name: string, typeLimits: string | null, type = 'compact') => {
        const schedule =
          type === 'constant'
            ? createDefaultScheduleConstant(name, 1, typeLimits)
            : createDefaultScheduleCompact(name, typeLimits);

        const action: ScheduleAction = {
          type: 'CREATE_SCHEDULE',
          timestamp: Date.now(),
          data: schedule,
          inverse: { scheduleId: schedule.id },
        };

        set((state) => ({
          schedules: [...state.schedules, schedule],
          selectedScheduleId: schedule.id,
          selectedPeriodId: schedule.periods[0]?.id || null,
          ...pushUndo(state, action),
        }));

        return schedule.id;
      },

      deleteSchedule: (id: string) => {
        const state = get();
        const schedule = state.schedules.find((s) => s.id === id);
        if (!schedule) return;

        const action: ScheduleAction = {
          type: 'DELETE_SCHEDULE',
          timestamp: Date.now(),
          data: { scheduleId: id },
          inverse: { schedule: cloneSchedule(schedule) },
        };

        set((state) => ({
          schedules: state.schedules.filter((s) => s.id !== id),
          selectedScheduleId: state.selectedScheduleId === id ? null : state.selectedScheduleId,
          selectedPeriodId: state.selectedScheduleId === id ? null : state.selectedPeriodId,
          ...pushUndo(state, action),
        }));
      },

      duplicateSchedule: (id: string, newName: string) => {
        const state = get();
        const original = state.schedules.find((s) => s.id === id);
        if (!original) return null;

        const duplicate = cloneSchedule(original);
        duplicate.id = generateId();
        duplicate.name = newName;
        delete duplicate.idfObjectId; // Clear IDF reference

        // Update period IDs
        for (const period of duplicate.periods) {
          period.id = generateId();
        }

        const action: ScheduleAction = {
          type: 'CREATE_SCHEDULE',
          timestamp: Date.now(),
          data: duplicate,
          inverse: { scheduleId: duplicate.id },
        };

        set((state) => ({
          schedules: [...state.schedules, duplicate],
          selectedScheduleId: duplicate.id,
          selectedPeriodId: duplicate.periods[0]?.id || null,
          ...pushUndo(state, action),
        }));

        return duplicate.id;
      },

      updateSchedule: (id: string, updates: Partial<ParsedSchedule>) => {
        const state = get();
        const schedule = state.schedules.find((s) => s.id === id);
        if (!schedule) return;

        const action: ScheduleAction = {
          type: 'UPDATE_SCHEDULE',
          timestamp: Date.now(),
          data: { scheduleId: id, updates },
          inverse: { schedule: cloneSchedule(schedule) },
        };

        set((state) => ({
          schedules: state.schedules.map((s) =>
            s.id === id ? { ...s, ...updates } : s
          ),
          ...pushUndo(state, action),
        }));
      },

      addPeriod: (scheduleId: string, periodData: Omit<SchedulePeriod, 'id'>) => {
        const state = get();
        const schedule = state.schedules.find((s) => s.id === scheduleId);
        if (!schedule) return null;

        const period: SchedulePeriod = {
          ...periodData,
          id: generateId(),
        };

        const action: ScheduleAction = {
          type: 'ADD_PERIOD',
          timestamp: Date.now(),
          data: { scheduleId, period },
          inverse: { scheduleId, periodId: period.id },
        };

        set((state) => ({
          schedules: state.schedules.map((s) =>
            s.id === scheduleId
              ? { ...s, periods: [...s.periods, period] }
              : s
          ),
          selectedPeriodId: period.id,
          ...pushUndo(state, action),
        }));

        return period.id;
      },

      deletePeriod: (scheduleId: string, periodId: string) => {
        const state = get();
        const schedule = state.schedules.find((s) => s.id === scheduleId);
        if (!schedule) return;

        const period = schedule.periods.find((p) => p.id === periodId);
        if (!period) return;

        // Don't delete if it's the only period
        if (schedule.periods.length <= 1) return;

        const action: ScheduleAction = {
          type: 'DELETE_PERIOD',
          timestamp: Date.now(),
          data: { scheduleId, periodId },
          inverse: { scheduleId, period: JSON.parse(JSON.stringify(period)) as SchedulePeriod },
        };

        set((state) => ({
          schedules: state.schedules.map((s) =>
            s.id === scheduleId
              ? { ...s, periods: s.periods.filter((p) => p.id !== periodId) }
              : s
          ),
          selectedPeriodId:
            state.selectedPeriodId === periodId
              ? schedule.periods.find((p) => p.id !== periodId)?.id || null
              : state.selectedPeriodId,
          ...pushUndo(state, action),
        }));
      },

      updatePeriod: (scheduleId: string, periodId: string, updates: Partial<SchedulePeriod>) => {
        const state = get();
        const schedule = state.schedules.find((s) => s.id === scheduleId);
        if (!schedule) return;

        const period = schedule.periods.find((p) => p.id === periodId);
        if (!period) return;

        const action: ScheduleAction = {
          type: 'UPDATE_PERIOD',
          timestamp: Date.now(),
          data: { scheduleId, periodId, updates },
          inverse: { scheduleId, periodId, period: JSON.parse(JSON.stringify(period)) as SchedulePeriod },
        };

        set((state) => ({
          schedules: state.schedules.map((s) =>
            s.id === scheduleId
              ? {
                  ...s,
                  periods: s.periods.map((p) =>
                    p.id === periodId ? { ...p, ...updates } : p
                  ),
                }
              : s
          ),
          ...pushUndo(state, action),
        }));
      },

      updateDayValues: (
        scheduleId: string,
        periodId: string,
        dayTypes: DayType[],
        values: TimeValue[]
      ) => {
        const state = get();
        const schedule = state.schedules.find((s) => s.id === scheduleId);
        if (!schedule) return;

        const period = schedule.periods.find((p) => p.id === periodId);
        if (!period) return;

        // Find existing day schedule or create new one
        const existingIndex = period.daySchedules.findIndex((ds) =>
          ds.dayTypes.length === dayTypes.length &&
          ds.dayTypes.every((dt) => dayTypes.includes(dt))
        );

        const oldDaySchedules = JSON.parse(JSON.stringify(period.daySchedules)) as DaySchedule[];

        let newDaySchedules: DaySchedule[];
        if (existingIndex >= 0) {
          newDaySchedules = period.daySchedules.map((ds, i) =>
            i === existingIndex ? { ...ds, values } : ds
          );
        } else {
          newDaySchedules = [...period.daySchedules, { dayTypes, values }];
        }

        const action: ScheduleAction = {
          type: 'UPDATE_DAY_VALUES',
          timestamp: Date.now(),
          data: { scheduleId, periodId, dayTypes, values },
          inverse: { scheduleId, periodId, daySchedules: oldDaySchedules },
        };

        set((state) => ({
          schedules: state.schedules.map((s) =>
            s.id === scheduleId
              ? {
                  ...s,
                  periods: s.periods.map((p) =>
                    p.id === periodId ? { ...p, daySchedules: newDaySchedules } : p
                  ),
                }
              : s
          ),
          ...pushUndo(state, action),
        }));
      },

      copyDayToOthers: (
        scheduleId: string,
        periodId: string,
        sourceDayTypes: DayType[],
        targetDayTypes: DayType[][]
      ) => {
        const state = get();
        const schedule = state.schedules.find((s) => s.id === scheduleId);
        if (!schedule) return;

        const period = schedule.periods.find((p) => p.id === periodId);
        if (!period) return;

        // Find source day schedule
        const sourceDs = period.daySchedules.find((ds) =>
          ds.dayTypes.some((dt) => sourceDayTypes.includes(dt))
        );
        if (!sourceDs) return;

        // Copy to each target
        for (const target of targetDayTypes) {
          get().updateDayValues(scheduleId, periodId, target, [...sourceDs.values]);
        }
      },

      setTemplateFilter: (filter: Partial<ScheduleEditorState['templateFilter']>) => {
        set((state) => ({
          templateFilter: { ...state.templateFilter, ...filter },
        }));
      },

      applyTemplate: (template: ScheduleTemplate, scheduleName: string) => {
        const id = generateId();
        const periodId = generateId();

        // Convert week pattern to day schedules
        const daySchedules: DaySchedule[] = [];

        // Check if all weekdays are the same
        const weekdayPatterns = [
          template.weekPattern.monday,
          template.weekPattern.tuesday,
          template.weekPattern.wednesday,
          template.weekPattern.thursday,
          template.weekPattern.friday,
        ];
        const allWeekdaysSame = weekdayPatterns.every(
          (p) => JSON.stringify(p) === JSON.stringify(template.weekPattern.monday)
        );

        // Check if weekend days are the same
        const weekendSame =
          JSON.stringify(template.weekPattern.saturday) ===
          JSON.stringify(template.weekPattern.sunday);

        if (allWeekdaysSame) {
          daySchedules.push({
            dayTypes: ['Weekdays'],
            values: [...template.weekPattern.monday],
          });
        } else {
          // Add individual weekday schedules
          const days: { dayType: DayType; pattern: TimeValue[] }[] = [
            { dayType: 'Monday', pattern: template.weekPattern.monday },
            { dayType: 'Tuesday', pattern: template.weekPattern.tuesday },
            { dayType: 'Wednesday', pattern: template.weekPattern.wednesday },
            { dayType: 'Thursday', pattern: template.weekPattern.thursday },
            { dayType: 'Friday', pattern: template.weekPattern.friday },
          ];

          for (const { dayType, pattern } of days) {
            daySchedules.push({
              dayTypes: [dayType],
              values: [...pattern],
            });
          }
        }

        if (weekendSame) {
          daySchedules.push({
            dayTypes: ['Weekends'],
            values: [...template.weekPattern.saturday],
          });
        } else {
          daySchedules.push({
            dayTypes: ['Saturday'],
            values: [...template.weekPattern.saturday],
          });
          daySchedules.push({
            dayTypes: ['Sunday'],
            values: [...template.weekPattern.sunday],
          });
        }

        // Add design days if present
        if (template.weekPattern.summerDesignDay) {
          daySchedules.push({
            dayTypes: ['SummerDesignDay'],
            values: [...template.weekPattern.summerDesignDay],
          });
        }

        if (template.weekPattern.winterDesignDay) {
          daySchedules.push({
            dayTypes: ['WinterDesignDay'],
            values: [...template.weekPattern.winterDesignDay],
          });
        }

        // Add holiday if present
        if (template.weekPattern.holiday) {
          daySchedules.push({
            dayTypes: ['Holidays'],
            values: [...template.weekPattern.holiday],
          });
        }

        const schedule: ParsedSchedule = {
          id,
          name: scheduleName,
          typeLimits: template.typeLimits,
          scheduleType: 'Schedule:Compact',
          periods: [
            {
              id: periodId,
              startDate: { month: 1, day: 1 },
              endDate: { month: 12, day: 31 },
              daySchedules,
            },
          ],
        };

        const action: ScheduleAction = {
          type: 'CREATE_SCHEDULE',
          timestamp: Date.now(),
          data: schedule,
          inverse: { scheduleId: id },
        };

        set((state) => ({
          schedules: [...state.schedules, schedule],
          selectedScheduleId: id,
          selectedPeriodId: periodId,
          ...pushUndo(state, action),
        }));

        return id;
      },

      copyToClipboard: (type: ScheduleClipboard['type'], data: ScheduleClipboard['data']) => {
        set({
          clipboard: {
            type,
            data: JSON.parse(JSON.stringify(data)) as ScheduleClipboard['data'],
          },
        });
      },

      pasteFromClipboard: (scheduleId: string, periodId?: string) => {
        const state = get();
        if (!state.clipboard) return;

        const schedule = state.schedules.find((s) => s.id === scheduleId);
        if (!schedule) return;

        switch (state.clipboard.type) {
          case 'day': {
            const dayData = state.clipboard.data as DaySchedule;
            const targetPeriodId = periodId || schedule.periods[0]?.id;
            if (targetPeriodId) {
              get().updateDayValues(scheduleId, targetPeriodId, dayData.dayTypes, dayData.values);
            }
            break;
          }
          case 'week': {
            const weekData = state.clipboard.data as DaySchedule[];
            const targetPeriodId = periodId || schedule.periods[0]?.id;
            if (targetPeriodId) {
              for (const ds of weekData) {
                get().updateDayValues(scheduleId, targetPeriodId, ds.dayTypes, ds.values);
              }
            }
            break;
          }
          // Add more cases as needed
        }
      },

      undo: () => {
        const state = get();
        if (state.undoStack.length === 0) return;

        const action = state.undoStack[state.undoStack.length - 1];
        if (!action) return;

        const inverse = action.inverse as Record<string, unknown>;

        // Apply inverse action
        switch (action.type) {
          case 'CREATE_SCHEDULE':
            set((s) => ({
              schedules: s.schedules.filter((sch) => sch.id !== (inverse['scheduleId'] as string)),
              selectedScheduleId:
                s.selectedScheduleId === (inverse['scheduleId'] as string)
                  ? null
                  : s.selectedScheduleId,
            }));
            break;

          case 'DELETE_SCHEDULE':
            set((s) => ({
              schedules: [...s.schedules, inverse['schedule'] as ParsedSchedule],
            }));
            break;

          case 'UPDATE_SCHEDULE':
            set((s) => ({
              schedules: s.schedules.map((sch) =>
                sch.id === (inverse['schedule'] as ParsedSchedule).id
                  ? (inverse['schedule'] as ParsedSchedule)
                  : sch
              ),
            }));
            break;

          case 'ADD_PERIOD':
            set((s) => ({
              schedules: s.schedules.map((sch) =>
                sch.id === (inverse['scheduleId'] as string)
                  ? {
                      ...sch,
                      periods: sch.periods.filter((p) => p.id !== (inverse['periodId'] as string)),
                    }
                  : sch
              ),
            }));
            break;

          case 'DELETE_PERIOD':
            set((s) => ({
              schedules: s.schedules.map((sch) =>
                sch.id === (inverse['scheduleId'] as string)
                  ? {
                      ...sch,
                      periods: [...sch.periods, inverse['period'] as SchedulePeriod],
                    }
                  : sch
              ),
            }));
            break;

          case 'UPDATE_PERIOD':
            set((s) => ({
              schedules: s.schedules.map((sch) =>
                sch.id === (inverse['scheduleId'] as string)
                  ? {
                      ...sch,
                      periods: sch.periods.map((p) =>
                        p.id === (inverse['periodId'] as string)
                          ? (inverse['period'] as SchedulePeriod)
                          : p
                      ),
                    }
                  : sch
              ),
            }));
            break;

          case 'UPDATE_DAY_VALUES':
            set((s) => ({
              schedules: s.schedules.map((sch) =>
                sch.id === (inverse['scheduleId'] as string)
                  ? {
                      ...sch,
                      periods: sch.periods.map((p) =>
                        p.id === (inverse['periodId'] as string)
                          ? { ...p, daySchedules: inverse['daySchedules'] as DaySchedule[] }
                          : p
                      ),
                    }
                  : sch
              ),
            }));
            break;
        }

        // Move action to redo stack
        set((s) => ({
          undoStack: s.undoStack.slice(0, -1),
          redoStack: [...s.redoStack, action],
          isDirty: true,
        }));
      },

      redo: () => {
        const state = get();
        if (state.redoStack.length === 0) return;

        const action = state.redoStack[state.redoStack.length - 1];
        if (!action) return;

        const data = action.data as Record<string, unknown>;

        // Reapply action
        switch (action.type) {
          case 'CREATE_SCHEDULE':
            set((s) => ({
              schedules: [...s.schedules, data as unknown as ParsedSchedule],
            }));
            break;

          case 'DELETE_SCHEDULE':
            set((s) => ({
              schedules: s.schedules.filter((sch) => sch.id !== (data['scheduleId'] as string)),
            }));
            break;

          case 'UPDATE_SCHEDULE':
            set((s) => ({
              schedules: s.schedules.map((sch) =>
                sch.id === (data['scheduleId'] as string)
                  ? { ...sch, ...(data['updates'] as Partial<ParsedSchedule>) }
                  : sch
              ),
            }));
            break;

          case 'ADD_PERIOD':
            set((s) => ({
              schedules: s.schedules.map((sch) =>
                sch.id === (data['scheduleId'] as string)
                  ? { ...sch, periods: [...sch.periods, data['period'] as SchedulePeriod] }
                  : sch
              ),
            }));
            break;

          case 'DELETE_PERIOD':
            set((s) => ({
              schedules: s.schedules.map((sch) =>
                sch.id === (data['scheduleId'] as string)
                  ? {
                      ...sch,
                      periods: sch.periods.filter((p) => p.id !== (data['periodId'] as string)),
                    }
                  : sch
              ),
            }));
            break;

          case 'UPDATE_DAY_VALUES': {
            const scheduleId = data['scheduleId'] as string;
            const periodId = data['periodId'] as string;
            const dayTypes = data['dayTypes'] as DayType[];
            const values = data['values'] as TimeValue[];

            set((s) => {
              const schedule = s.schedules.find((sch) => sch.id === scheduleId);
              if (!schedule) return s;

              const period = schedule.periods.find((p) => p.id === periodId);
              if (!period) return s;

              const existingIndex = period.daySchedules.findIndex((ds) =>
                ds.dayTypes.length === dayTypes.length &&
                ds.dayTypes.every((dt) => dayTypes.includes(dt))
              );

              let newDaySchedules: DaySchedule[];
              if (existingIndex >= 0) {
                newDaySchedules = period.daySchedules.map((ds, i) =>
                  i === existingIndex ? { ...ds, values } : ds
                );
              } else {
                newDaySchedules = [...period.daySchedules, { dayTypes, values }];
              }

              return {
                schedules: s.schedules.map((sch) =>
                  sch.id === scheduleId
                    ? {
                        ...sch,
                        periods: sch.periods.map((p) =>
                          p.id === periodId ? { ...p, daySchedules: newDaySchedules } : p
                        ),
                      }
                    : sch
                ),
              };
            });
            break;
          }
        }

        // Move action back to undo stack
        set((s) => ({
          redoStack: s.redoStack.slice(0, -1),
          undoStack: [...s.undoStack, action],
          isDirty: true,
        }));
      },

      canUndo: () => get().undoStack.length > 0,

      canRedo: () => get().redoStack.length > 0,

      syncToDocument: (
        updateObject: (objectId: string, updates: Partial<IDFObject>) => void,
        addObject: (obj: Omit<IDFObject, 'id'>) => string,
        removeObject: (objectId: string) => void
      ) => {
        const state = get();
        const currentIdfObjectIds = new Set<string>();
        // Track new schedule-to-IDF-ID mappings to batch update at the end
        const newScheduleIdMappings = new Map<string, string>();

        // Process each schedule in the store
        for (const schedule of state.schedules) {
          // Only sync Schedule:Compact and Schedule:Constant (supported types)
          if (schedule.scheduleType !== 'Schedule:Compact' && schedule.scheduleType !== 'Schedule:Constant') {
            // Keep track of unsupported schedules that exist in IDF
            if (schedule.idfObjectId) {
              currentIdfObjectIds.add(schedule.idfObjectId);
            }
            continue;
          }

          const idfObject = scheduleToIDFObject(schedule);

          if (schedule.idfObjectId) {
            // Update existing IDF object
            currentIdfObjectIds.add(schedule.idfObjectId);
            updateObject(schedule.idfObjectId, {
              className: idfObject.className,
              fields: idfObject.fields,
            });
          } else {
            // Add new IDF object and store its ID for batch update
            const newId = addObject({
              className: idfObject.className,
              fields: idfObject.fields,
              leadingComments: [],
            });
            // Track the mapping for batch update
            newScheduleIdMappings.set(schedule.id, newId);
            currentIdfObjectIds.add(newId);
          }
        }

        // Remove schedules that were deleted (existed in original but not in current)
        for (const originalId of state.originalIdfObjectIds) {
          if (!currentIdfObjectIds.has(originalId)) {
            removeObject(originalId);
          }
        }

        // Batch update: assign IDF object IDs to new schedules and update tracking
        set((s) => ({
          schedules: s.schedules.map((sch) => {
            const newIdfId = newScheduleIdMappings.get(sch.id);
            return newIdfId ? { ...sch, idfObjectId: newIdfId } : sch;
          }),
          originalIdfObjectIds: currentIdfObjectIds,
          isDirty: false,
        }));
      },

      getScheduleInfo: (schedule: ParsedSchedule, document: IDFDocument | null): ScheduleInfo => {
        const references = findScheduleReferences(schedule.name, document);
        return {
          id: schedule.id,
          name: schedule.name,
          type: schedule.scheduleType,
          typeLimits: schedule.typeLimits,
          category: categorizeSchedule(schedule, references),
          usageCount: references.length,
          referencedBy: references,
        };
      },

      getSchedule: (id: string) => {
        return get().schedules.find((s) => s.id === id);
      },

      getTypeLimits: (name: string) => {
        return get().typeLimits.find((tl) => tl.name === name);
      },

      clear: () => {
        set({
          schedules: [],
          typeLimits: [],
          selectedScheduleId: null,
          selectedPeriodId: null,
          selectedDayType: null,
          undoStack: [],
          redoStack: [],
          clipboard: null,
          isDirty: false,
          originalIdfObjectIds: new Set<string>(),
        });
      },

      markClean: () => {
        set({ isDirty: false });
      },
    }),
    { name: 'ScheduleStore' }
  )
);

/**
 * Selector for selected schedule
 */
export const useSelectedSchedule = () => {
  const selectedScheduleId = useScheduleStore((state) => state.selectedScheduleId);
  const schedules = useScheduleStore((state) => state.schedules);
  return schedules.find((s) => s.id === selectedScheduleId);
};

/**
 * Selector for selected period
 */
export const useSelectedPeriod = () => {
  const selectedScheduleId = useScheduleStore((state) => state.selectedScheduleId);
  const selectedPeriodId = useScheduleStore((state) => state.selectedPeriodId);
  const schedules = useScheduleStore((state) => state.schedules);

  const schedule = schedules.find((s) => s.id === selectedScheduleId);
  return schedule?.periods.find((p) => p.id === selectedPeriodId);
};

/**
 * Selector for schedules grouped by category
 */
export const useSchedulesByCategory = () => {
  const schedules = useScheduleStore((state) => state.schedules);
  const getScheduleInfo = useScheduleStore((state) => state.getScheduleInfo);

  const grouped = new Map<ScheduleCategory, ParsedSchedule[]>();

  for (const schedule of schedules) {
    const info = getScheduleInfo(schedule, null);
    const existing = grouped.get(info.category) || [];
    grouped.set(info.category, [...existing, schedule]);
  }

  return grouped;
};
